import { useEffect, useState } from 'react';
import { Activity, MapPin } from 'lucide-react';
import api from '../lib/api';

const ACTION_LABEL = {
  claimed: 'Claimed', verified_by_email_code: 'Verified (OTP)', claim_approved_by_admin: 'Approved by admin', signals_updated: 'Signals / BDAF updated',
  correction: 'Record corrected', links_updated: 'Links updated', listing_added: 'Listing added', listing_updated: 'Listing updated', listing_deleted: 'Listing deleted', enquiry_sent: 'Enquiry sent',
  verified_by_document: 'Verified (document)', signal_disconnected: 'Scan signal disconnected', signal_reconnected: 'Scan signal restored',
};
const TONE = { listing_deleted: 'text-danger', claimed: 'text-brand-orange', verified_by_email_code: 'text-forest', verified_by_document: 'text-forest', claim_approved_by_admin: 'text-forest', signals_updated: 'text-ink', signal_disconnected: 'text-status-amber' };

const Diff = ({ changes }) => {
  if (!changes || Object.keys(changes).length === 0) return <span className="text-ink/40">—</span>;
  if (changes.bdaf_before !== undefined) return <span>BDAF <strong>{changes.bdaf_before}</strong> → <strong className="text-forest">{changes.bdaf_after}</strong> · {Object.keys(changes.after || {}).join(', ') || 'no signals'}</span>;
  if (changes.listing) return <span>{changes.listing.name} <span className="text-ink/50">({changes.listing.category})</span></span>;
  if (changes.before && changes.after) {
    const keys = [...new Set([...Object.keys(changes.before), ...Object.keys(changes.after)])].filter(k => JSON.stringify(changes.before[k]) !== JSON.stringify(changes.after[k]));
    return <span>{keys.slice(0, 4).map(k => <span key={k} className="mr-2">{k}: <s className="text-ink/40">{String(changes.before[k] ?? '∅').slice(0, 24)}</s> → <strong>{String(changes.after[k] ?? '∅').slice(0, 24)}</strong></span>)}{keys.length > 4 && `+${keys.length - 4} more`}</span>;
  }
  return <span className="text-ink/60">{JSON.stringify(changes).slice(0, 120)}</span>;
};

export const OwnerActivityLog = () => {
  const [data, setData] = useState(null);
  const [action, setAction] = useState('');
  useEffect(() => { api.get('/admin/owner-activity', { params: { limit: 200, action } }).then(r => setData(r.data)).catch(() => setData({ items: [], counts: {}, total: 0 })); }, [action]);
  if (!data) return <div className="font-body text-sm text-ink/50 p-4">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="owner-activity-log">
      <div className="solid-card p-4 flex flex-wrap items-center gap-3">
        <Activity className="w-4 h-4 text-brand-orange" />
        <p className="font-display text-sm font-extrabold text-ink">Owner activity · {data.total.toLocaleString()} events</p>
        <select value={action} onChange={e => setAction(e.target.value)} className="ml-auto h-8 rounded-[6px] border border-ink/15 bg-white px-2 font-body text-xs" data-testid="activity-filter">
          <option value="">All actions</option>
          {Object.entries(data.counts).map(([k, n]) => <option key={k} value={k}>{ACTION_LABEL[k] || k} ({n})</option>)}
        </select>
      </div>
      <div className="solid-card overflow-x-auto">
        <table className="w-full font-body text-xs" data-testid="activity-table">
          <thead><tr className="text-left text-ink/40 uppercase tracking-wider text-[10px] border-b border-ink/5">
            <th className="p-3">When</th><th className="p-3">Owner</th><th className="p-3">Business</th><th className="p-3">Action</th><th className="p-3">What changed</th><th className="p-3">Location</th>
          </tr></thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-ink/40">No activity yet</td></tr>}
            {data.items.map((r, i) => (
              <tr key={`${r.ts}-${i}`} className="border-b border-ink/5 align-top" data-testid={`activity-row-${r.action}`}>
                <td className="p-3 whitespace-nowrap text-ink/60">{new Date(r.ts).toLocaleString()}</td>
                <td className="p-3"><div className="text-ink">{r.user_email}</div>{r.user_name && <div className="text-ink/40">{r.user_name}</div>}</td>
                <td className="p-3 max-w-[200px] truncate text-ink">{r.company_name || r.business_id}</td>
                <td className={`p-3 whitespace-nowrap font-semibold ${TONE[r.action] || 'text-ink/70'}`}>{ACTION_LABEL[r.action] || r.action}</td>
                <td className="p-3 max-w-[360px]"><Diff changes={r.changes} /></td>
                <td className="p-3 whitespace-nowrap text-ink/60"><MapPin className="w-3 h-3 inline mr-1 -mt-0.5" />{r.geo?.city ? `${r.geo.city}, ${r.geo.country}` : 'Unknown'}<div className="text-ink/35 font-mono text-[10px]">{r.ip}</div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
